// These are the pages you can go to.
// They are all wrapped in the App component, which should contain the navbar etc
// See http://blog.mxstbr.com/2016/01/react-apps-with-pages for more information
// about the code splitting business
import React from 'react';

import { handleAppUpdate } from 'utils/handleAppUpdate';
import { getAsyncInjectors } from 'utils/asyncInjectors';
import auth from 'utils/auth';
import ErrorState from 'components/ErrorState';
import appSagas from 'containers/App/sagas';
import profileSearchSagas from 'containers/ProfileInput/sagas';
import teamListSagas from 'containers/NestedTeamList/sagas';
import goalProgressOverTimeModalSagas from 'containers/GoalProgressOverTimeModal/sagas';
import keyResultProgressOverTimeModalSagas from 'containers/KeyResultProgressOverTimeModal/sagas';

const errorLoading = cb => err => {
  console.error('Dynamic page loading failed', err); // eslint-disable-line no-console
  cb(null, () => <ErrorState javascriptError={err} />);
};

const loadModule = cb => componentModule => {
  cb(null, componentModule.default);
};

export default function createRoutes(store) {
  // Create reusable async injectors using getAsyncInjectors factory
  const { injectReducer, injectSagas } = getAsyncInjectors(store);

  injectSagas(appSagas);
  injectSagas(profileSearchSagas);
  injectSagas(teamListSagas);
  injectSagas(goalProgressOverTimeModalSagas);
  injectSagas(keyResultProgressOverTimeModalSagas);

  return [
    {
      path: '/',
      onEnter: (nextState, replace) => {
        if (auth.isLoggedIn()) {
          replace('/goals');
        } else {
          replace('/login');
        }
      },
    },
    {
      path: '/register',
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/RegisterPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/login',
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/LoginPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/forgot',
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/ForgotPasswordPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/reset/:token',
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/ResetPasswordPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/invite/:token',
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/AcceptInvitationPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/integrations/planner/sign_in',
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/PlannerAuthenticationRedirectPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/goals',
      indexRoute: {
        onEnter: (nextState, replace) => replace('/goals/overview/me'),
      },
      childRoutes: [
        {
          path: '/goals/overview/me',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/GoalOverviewMePage'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/overview/teams/:teamId',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/GoalsOverviewTeamPage'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/overview/business_units/:businessUnitId',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/GoalsOverviewBusinessUnitPage'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/overview/organization',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/GoalsOverviewOrganizationPage'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/overview/filter/:customGoalFilterId',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/GoalsOverviewFilterPage'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/explorer',
          onEnter(nextState, replace, cb) {
            auth.requireAuth(nextState, replace);

            const importModules = Promise.all([
              handleAppUpdate(() => import('pages/GoalExplorerPage/sagas')),
            ]);

            importModules.then(importedSagas => {
              if (this.loadedSagas) {
                cb();
                return;
              }

              this.loadedSagas = importedSagas.reduce(
                (loadedSagas, importedSaga) =>
                  loadedSagas.concat(injectSagas(importedSaga.default)),
                []
              );
              cb();
            });

            importModules.catch(errorLoading(cb));
          },
          onLeave() {
            if (this.loadedSagas) {
              this.loadedSagas.forEach(saga => saga.cancel());
              delete this.loadedSagas;
            }
          },
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              handleAppUpdate(() => import('pages/GoalExplorerPage')),
              handleAppUpdate(() => import('pages/GoalExplorerPage/reducer')),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([component, reducer]) => {
              injectReducer('goalExplorerPage', reducer.default);
              renderRoute(component);
            });

            importModules.catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/download_export',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/DownloadExportPage'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/graph',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            handleAppUpdate(() => import('pages/GoalGraphOverlay'))
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/drafting-module',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/OKRDraftingPage'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/:goalId',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/GoalPage'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/initiatives/:initiativeId',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/InitiativePage'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/kpis/:kpiId',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/KpiPage'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/keyResults/:keyResultId',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/KeyResultPage'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/:goalId/key_results/new',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/CreateKeyResultOverlay'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/goals/:goalId/relationships',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/EditRelationshipsOverlay'))
              .then(component => {
                renderRoute(component);
              })
              .catch(errorLoading(cb));
          },
        },
        {
          // TODO: Deprecated check_in route; remove when not used by users any more
          path: '/goals/:goalId/check_in',
          onEnter: (nextState, replace) => {
            auth.requireAuth(nextState, replace);

            const { goalId } = nextState.params;
            replace(`/goals/${goalId}/status_updates/new`);
          },
        },
        {
          path: '/goals/:goalId/status_updates/new',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/CreateStatusUpdateOverlay'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/check-ins',
      indexRoute: {
        onEnter: (nextState, replace) => replace('/check-ins/upcoming'),
      },
      childRoutes: [
        {
          path: '/check-ins/:filter',
          onEnter(nextState, replace, cb) {
            auth.requireAuth(nextState, replace);

            const importModules = Promise.all([
              handleAppUpdate(() => import('pages/CheckInsOverviewPage/sagas')),
              handleAppUpdate(() => import('containers/CheckInsLayout/sagas')),
            ]);

            importModules.then(importedSagas => {
              if (this.loadedSagas) {
                cb();
                return;
              }

              this.loadedSagas = importedSagas.reduce(
                (loadedSagas, importedSaga) =>
                  loadedSagas.concat(injectSagas(importedSaga.default)),
                []
              );

              cb();
            });

            importModules.catch(errorLoading(cb));
          },
          onChange(nextState, replace, cb) {
            if (!this.loadedSagas) {
              const importModules = Promise.all([
                handleAppUpdate(() =>
                  import('pages/CheckInsOverviewPage/sagas')
                ),
                handleAppUpdate(() =>
                  import('containers/CheckInsLayout/sagas')
                ),
              ]);

              importModules.then(importedSagas => {
                if (this.loadedSagas) {
                  cb();
                  return;
                }

                this.loadedSagas = importedSagas.reduce(
                  (loadedSagas, importedSaga) =>
                    loadedSagas.concat(injectSagas(importedSaga.default)),
                  []
                );

                cb();
              });

              importModules.catch(errorLoading(cb));
            }
          },
          onLeave() {
            if (this.loadedSagas) {
              this.loadedSagas.forEach(saga => saga.cancel());
              delete this.loadedSagas;
            }
          },
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              handleAppUpdate(() => import('pages/CheckInsOverviewPage')),
              handleAppUpdate(() =>
                import('pages/CheckInsOverviewPage/reducer')
              ),
              handleAppUpdate(() =>
                import('containers/CheckInsLayout/reducer')
              ),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([
                checkInsOverviewPage,
                checkInsOverviewPageReducer,
                checkInsLayoutReducer,
              ]) => {
                injectReducer(
                  'checkInsOverviewPage',
                  checkInsOverviewPageReducer.default
                );
                injectReducer('checkInsLayout', checkInsLayoutReducer.default);
                renderRoute(checkInsOverviewPage);
              }
            );

            importModules.catch(errorLoading(cb));
          },
        },
        {
          path: '/check-ins/:checkInId/tasks-and-priorities',
          onEnter(nextState, replace, cb) {
            auth.requireAuth(nextState, replace);

            const importModules = Promise.all([
              handleAppUpdate(() => import('containers/CheckInsLayout/sagas')),
            ]);

            importModules.then(importedSagas => {
              if (this.loadedSagas) {
                cb();
                return;
              }

              this.loadedSagas = importedSagas.reduce(
                (loadedSagas, importedSaga) =>
                  loadedSagas.concat(injectSagas(importedSaga.default)),
                []
              );

              cb();
            });

            importModules.catch(errorLoading(cb));
          },
          onChange(nextState, replace, cb) {
            if (!this.loadedSagas) {
              const importModules = Promise.all([
                handleAppUpdate(() =>
                  import('containers/CheckInsLayout/sagas')
                ),
              ]);

              importModules.then(importedSagas => {
                if (this.loadedSagas) {
                  cb();
                  return;
                }

                this.loadedSagas = importedSagas.reduce(
                  (loadedSagas, importedSaga) =>
                    loadedSagas.concat(injectSagas(importedSaga.default)),
                  []
                );

                cb();
              });

              importModules.catch(errorLoading(cb));
            }
          },
          onLeave() {
            if (this.loadedSagas) {
              this.loadedSagas.forEach(saga => saga.cancel());
              delete this.loadedSagas;
            }
          },
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              handleAppUpdate(() =>
                import('containers/CheckInsLayout/reducer')
              ),
            ]);

            const renderRoute = loadModule(cb);

            import('pages/CheckInTeamMemberReportsPage')
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));

            importModules.then(([checkInsLayoutReducer]) => {
              injectReducer('checkInsLayout', checkInsLayoutReducer.default);
            });

            importModules.catch(errorLoading(cb));
          },
        },
        {
          path: '/check-ins/:checkInId/tasks-and-priorities/me/edit',
          onEnter(nextState, replace, cb) {
            auth.requireAuth(nextState, replace);

            const importModules = Promise.all([
              handleAppUpdate(() =>
                import('pages/CheckInTeamMemberReportEditPage/sagas')
              ),
              handleAppUpdate(() => import('containers/CheckInsLayout/sagas')),
            ]);

            importModules.then(importedSagas => {
              if (this.loadedSagas) {
                cb();
                return;
              }

              this.loadedSagas = importedSagas.reduce(
                (loadedSagas, importedSaga) =>
                  loadedSagas.concat(injectSagas(importedSaga.default)),
                []
              );

              cb();
            });

            importModules.catch(errorLoading(cb));
          },
          onChange(nextState, replace, cb) {
            if (!this.loadedSagas) {
              const importModules = Promise.all([
                handleAppUpdate(() =>
                  import('pages/CheckInTeamMemberReportEditPage/sagas')
                ),
                handleAppUpdate(() =>
                  import('containers/CheckInsLayout/sagas')
                ),
              ]);

              importModules.then(importedSagas => {
                if (this.loadedSagas) {
                  cb();
                  return;
                }

                this.loadedSagas = importedSagas.reduce(
                  (loadedSagas, importedSaga) =>
                    loadedSagas.concat(injectSagas(importedSaga.default)),
                  []
                );

                cb();
              });

              importModules.catch(errorLoading(cb));
            }
          },
          onLeave() {
            if (this.loadedSagas) {
              this.loadedSagas.forEach(saga => saga.cancel());
              delete this.loadedSagas;
            }
          },
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              handleAppUpdate(() =>
                import('pages/CheckInTeamMemberReportEditPage')
              ),
              handleAppUpdate(() =>
                import('pages/CheckInTeamMemberReportEditPage/reducer')
              ),
              handleAppUpdate(() =>
                import('containers/CheckInsLayout/reducer')
              ),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(
              ([
                checkInTeamMemberReportEditPage,
                checkInTeamMemberReportEditPageReducer,
                checkInsLayoutReducer,
              ]) => {
                injectReducer(
                  'checkInTeamMemberReportEditPage',
                  checkInTeamMemberReportEditPageReducer.default
                );
                injectReducer('checkInsLayout', checkInsLayoutReducer.default);
                renderRoute(checkInTeamMemberReportEditPage);
              }
            );

            importModules.catch(errorLoading(cb));
          },
        },
        {
          path: '/check-ins/:checkInId/team-member-reports/:teamMemberReportId/status-updates',
          onEnter(nextState, replace, cb) {
            auth.requireAuth(nextState, replace);

            const importModules = Promise.all([
              handleAppUpdate(() => import('containers/CheckInsLayout/sagas')),
            ]);

            importModules.then(importedSagas => {
              if (this.loadedSagas) {
                cb();
                return;
              }

              this.loadedSagas = importedSagas.reduce(
                (loadedSagas, importedSaga) =>
                  loadedSagas.concat(injectSagas(importedSaga.default)),
                []
              );
              cb();
            });

            importModules.catch(errorLoading(cb));
          },
          onLeave() {
            if (this.loadedSagas) {
              this.loadedSagas.forEach(saga => saga.cancel());
              delete this.loadedSagas;
            }
          },
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              handleAppUpdate(() =>
                import('pages/CheckInTeamMemberReportStatusUpdatesPage')
              ),
              handleAppUpdate(() =>
                import('containers/CheckInsLayout/reducer')
              ),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([component, checkInsLayoutReducer]) => {
              injectReducer('checkInsLayout', checkInsLayoutReducer.default);
              renderRoute(component);
            });

            importModules.catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/users/:id',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/ProfilePage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/profile',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/ProfilePage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/confirmation',
      getComponent(location, cb) {
        handleAppUpdate(() => import('pages/ConfirmationRoute'))
          .then(loadModule(cb))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/settings/profile',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/SettingsProfilePage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/account',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/SettingsAccountPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/my-connections',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/SettingsConnectionsPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/users',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          const renderRoute = loadModule(cb);

          handleAppUpdate(() => import('pages/SettingsUsersPage'))
            .then(component => renderRoute(component))
            .catch(errorLoading(cb));
        },
      },
      childRoutes: [
        {
          path: '/settings/users/download_export',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/DownloadExportPage'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/users/:userId',
          onEnter(nextState, replace, cb) {
            auth.requireAuth(nextState, replace);

            const importModules = handleAppUpdate(() =>
              import('pages/SettingsUserPage/sagas')
            );

            importModules.then(sagas => {
              if (this.loadedSagas) {
                cb();
                return;
              }

              this.loadedSagas = injectSagas(sagas.default);
              cb();
            });

            importModules.catch(errorLoading(cb));
          },
          onLeave() {
            if (this.loadedSagas) {
              this.loadedSagas.forEach(saga => saga.cancel());
              delete this.loadedSagas;
            }
          },
          getComponent(nextState, cb) {
            const importModules = Promise.all([
              handleAppUpdate(() => import('pages/SettingsUserPage')),
              handleAppUpdate(() => import('pages/SettingsUserPage/reducer')),
            ]);

            const renderRoute = loadModule(cb);

            importModules.then(([component, reducer]) => {
              injectReducer('settingsUserPage', reducer.default);
              renderRoute(component);
            });

            importModules.catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/settings/business_units/new',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/SettingsBusinessUnitCreatePage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/business_units/:businessUnitId',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/SettingsBusinessUnitPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/business_units/:businessUnitId/edit',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/SettingsBusinessUnitEditPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/business_units/:businessUnitId/modify_members',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() =>
          import('pages/SettingsBusinessUnitModifyMembersPage')
        )
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/teams',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          const renderRoute = loadModule(cb);

          handleAppUpdate(() => import('pages/SettingsTeamsPage'))
            .then(component => renderRoute(component))
            .catch(errorLoading(cb));
        },
      },
      childRoutes: [
        {
          path: '/settings/teams/download_export',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/DownloadExportPage'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/teams/new',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/SettingsTeamCreatePage'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/teams/:teamId',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/SettingsTeamPage'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/teams/:teamId/edit',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/SettingsTeamEditPage'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/settings/audit_logs',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          const renderRoute = loadModule(cb);

          handleAppUpdate(() => import('pages/SettingsAuditLogsPage'))
            .then(component => renderRoute(component))
            .catch(errorLoading(cb));
        },
      },
      childRoutes: [
        {
          path: '/settings/audit_logs/download_export',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/DownloadExportPage'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/settings/cycles',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          const renderRoute = loadModule(cb);

          handleAppUpdate(() => import('pages/SettingsCyclesPage'))
            .then(component => renderRoute(component))
            .catch(errorLoading(cb));
        },
      },
      childRoutes: [
        {
          path: '/settings/cycles/new',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/SettingsCycleCreatePage'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/cycles/:cycleId/edit',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/SettingsCycleEditPage'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/settings/api_clients',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          handleAppUpdate(() => import('pages/SettingsApiClientPage'))
            .then(loadModule(cb))
            .catch(errorLoading(cb));
        },
      },
    },
    {
      path: '/settings/webhooks',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          handleAppUpdate(() =>
            import('pages/SettingsIntegrationsWebhooksPage')
          )
            .then(loadModule(cb))
            .catch(errorLoading(cb));
        },
      },
      childRoutes: [
        {
          path: '/settings/webhooks/new',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsIntegrationsWebhookFormPage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/webhooks/:webhookSubscriptionId/edit',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsIntegrationsWebhookFormPage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/settings/conversation_starters',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          handleAppUpdate(() =>
            import('pages/SettingsConversationStartersPage')
          )
            .then(loadModule(cb))
            .catch(errorLoading(cb));
        },
      },
      childRoutes: [
        {
          path: '/settings/conversation_starters/new',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsConversationStarterFormPage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/conversation_starters/:id/edit',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsConversationStarterFormPage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/settings/companion_contexts',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          handleAppUpdate(() => import('pages/SettingsCompanionContextsPage'))
            .then(loadModule(cb))
            .catch(errorLoading(cb));
        },
      },
      childRoutes: [
        {
          path: '/settings/companion_contexts/new',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsCompanionContextFormPage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/companion_contexts/:id/edit',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsCompanionContextFormPage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/settings/nudges',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          handleAppUpdate(() => import('pages/SettingsNudgesPage'))
            .then(loadModule(cb))
            .catch(errorLoading(cb));
        },
      },
      childRoutes: [
        {
          path: '/settings/nudges/:id/edit',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            handleAppUpdate(() => import('pages/SettingsNudgeFormPage'))
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/settings/initiative_types',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          handleAppUpdate(() => import('pages/SettingsInitiativeTypesPage'))
            .then(loadModule(cb))
            .catch(errorLoading(cb));
        },
      },
    },
    {
      path: '/settings/strategic_element_categories',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          handleAppUpdate(() =>
            import('pages/SettingsStrategicElementCategoriesPage')
          )
            .then(loadModule(cb))
            .catch(errorLoading(cb));
        },
      },
    },
    {
      path: '/sign_in',
      getComponent(location, cb) {
        handleAppUpdate(() => import('pages/ChangePasswordRoute'))
          .then(loadModule(cb))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/goals/:goalId/stakeholders',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/GoalCollaboratorsOverlay'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/notifications',
      onEnter: auth.requireAuth,
      getComponent(location, cb) {
        handleAppUpdate(() => import('pages/SettingsNotificationsPage'))
          .then(loadModule(cb))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/single_sign_on',
      onEnter(nextState, replace, cb) {
        const importModules = handleAppUpdate(() =>
          import('pages/SingleSignOnRoute/sagas')
        );

        importModules.then(sagas => {
          if (this.loadedSagas) {
            cb();
            return;
          }

          this.loadedSagas = injectSagas(sagas.default);
          cb();
        });

        importModules.catch(errorLoading(cb));
      },
      onLeave() {
        if (this.loadedSagas) {
          this.loadedSagas.forEach(saga => saga.cancel());
          delete this.loadedSagas;
        }
      },
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          handleAppUpdate(() => import('pages/SingleSignOnRoute')),
          handleAppUpdate(() => import('pages/SingleSignOnRoute/reducer')),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([component, reducer]) => {
          injectReducer('singleSignOnRoute', reducer.default);
          renderRoute(component);
        });

        importModules.catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/labels',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/SettingsLabelsPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/invite/new',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/SettingsInviteCreatePage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/settings/custom-goal-filters',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          handleAppUpdate(() => import('pages/SettingsCustomGoalFiltersPage'))
            .then(loadModule(cb))
            .catch(errorLoading(cb));
        },
      },
      childRoutes: [
        {
          path: '/settings/custom-goal-filters/new',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsCustomGoalFilterCreatePage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/custom-goal-filters/:customGoalFilterId/edit',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsCustomGoalFilterEditPage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/analytics',
      indexRoute: {
        onEnter: (nextState, replace) => replace('/analytics/reporting-hub'),
      },
      childRoutes: [
        {
          path: '/analytics/reporting-hub',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/AnalyticsReportingHubPage'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/analytics/organization-summary',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() =>
              import('pages/AnalyticsOrganizationSummaryReport')
            )
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/analytics/cycle-steering-report',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/AnalyticsCycleSteeringReport'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/analytics/strategy-execution-report',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() =>
              import('pages/AnalyticsStrategyExecutionReport')
            )
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/analytics/maturity-report',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/AnalyticsMaturityReport'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/analytics/process-insights-report',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() =>
              import('pages/AnalyticsProcessInsightsReport')
            )
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/analytics/business-review-report',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/AnalyticsBusinessReviewReport'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/analytics/management-planning-summary',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() =>
              import('pages/AnalyticsManagementPlanningSummary')
            )
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/analytics/management-review-summary',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() =>
              import('pages/AnalyticsManagementReviewSummary')
            )
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/analytics/other-reports',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() => import('pages/AnalyticsOtherReports'))
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
      ],
    },
    {
      path: '/settings/connections',
      indexRoute: {
        onEnter: auth.requireAuth,
        getComponent(nextState, cb) {
          const renderRoute = loadModule(cb);

          handleAppUpdate(() => import('pages/SettingsIntegrationsPage'))
            .then(component => renderRoute(component))
            .catch(errorLoading(cb));
        },
      },
      childRoutes: [
        {
          path: '/settings/connections/personio',
          onEnter: auth.requireAuth,
          getComponent(nextState, cb) {
            const renderRoute = loadModule(cb);

            handleAppUpdate(() =>
              import('pages/SettingsIntegrationsPersonioPage')
            )
              .then(component => renderRoute(component))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/connections/slack',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() => import('pages/SettingsIntegrationsSlackPage'))
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/connections/scim',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() => import('pages/SettingsIntegrationsScimPage'))
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/connections/saml',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() => import('pages/SettingsIntegrationsSamlPage'))
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/connections/teams',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() => import('pages/SettingsIntegrationsTeamsPage'))
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/connections/planner',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsIntegrationsPlannerPage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/connections/jira',
          indexRoute: {
            onEnter: auth.requireAuth,
            getComponent(location, cb) {
              handleAppUpdate(() =>
                import('pages/SettingsIntegrationsJiraPage')
              )
                .then(loadModule(cb))
                .catch(errorLoading(cb));
            },
          },
          childRoutes: [
            {
              path: '/settings/connections/jira/new',
              onEnter: auth.requireAuth,
              getComponent(nextState, cb) {
                handleAppUpdate(() =>
                  import('pages/SettingsIntegrationsJiraCreatePage')
                )
                  .then(loadModule(cb))
                  .catch(errorLoading(cb));
              },
            },
            {
              path: '/settings/connections/jira/:jiraIntegrationId/edit',
              onEnter: auth.requireAuth,
              getComponent(nextState, cb) {
                handleAppUpdate(() =>
                  import('pages/SettingsIntegrationsJiraEditPage')
                )
                  .then(loadModule(cb))
                  .catch(errorLoading(cb));
              },
            },
          ],
        },
        {
          path: '/settings/connections/azure',
          indexRoute: {
            onEnter: auth.requireAuth,
            getComponent(location, cb) {
              handleAppUpdate(() =>
                import('pages/SettingsIntegrationsAzurePage')
              )
                .then(loadModule(cb))
                .catch(errorLoading(cb));
            },
          },
          childRoutes: [
            {
              path: '/settings/connections/azure/new',
              onEnter: auth.requireAuth,
              getComponent(nextState, cb) {
                handleAppUpdate(() =>
                  import('pages/SettingsIntegrationsAzureCreatePage')
                )
                  .then(loadModule(cb))
                  .catch(errorLoading(cb));
              },
            },
            {
              path: '/settings/connections/azure/:azureIntegrationId/edit',
              onEnter: auth.requireAuth,
              getComponent(nextState, cb) {
                handleAppUpdate(() =>
                  import('pages/SettingsIntegrationsAzureEditPage')
                )
                  .then(loadModule(cb))
                  .catch(errorLoading(cb));
              },
            },
          ],
        },
        {
          path: '/settings/connections/powerbi',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() =>
              import(
                'pages/SettingsIntegrationsDataSourcePage/SettingsIntegrationsPowerBIPage'
              )
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/connections/excel',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() =>
              import(
                'pages/SettingsIntegrationsDataSourcePage/SettingsIntegrationsExcelPage'
              )
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/connections/mcp',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() => import('pages/SettingsIntegrationsMcpPage'))
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/connections/mcp/new',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsIntegrationsMcpPage/McpAppCreatePage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
        {
          path: '/settings/connections/mcp/:id',
          onEnter: auth.requireAuth,
          getComponent(location, cb) {
            handleAppUpdate(() =>
              import('pages/SettingsIntegrationsMcpPage/McpAppDetailPage')
            )
              .then(loadModule(cb))
              .catch(errorLoading(cb));
          },
        },
      ],
    },

    {
      path: '/teams/:teamId(/goal/:goalId)/contribution_request/create',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() =>
          import('pages/TeamsContributionRequestCreatePage')
        )
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },
    {
      path: '/teams/:teamId/contribution_request/:contributionRequestId',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/TeamsContributionRequestPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/conversations',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        import('pages/ConversationsOverviewPage')
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/conversations_new/:conversationId',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/ConversationPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/market-insights',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        import('pages/MarketInsightsPage')
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/chat',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('features/aiCompanion/ChatRedirect'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/conversations/new',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        import('pages/CreateConversationOverlay')
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/conversations/:conversationId',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/PrepareConversationsPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/conversations/edit/:conversationId',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/EditConversationOverlay'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/note/:noteId',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/NotesLandingPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/strategicElements/:strategicElementId',
      onEnter: auth.requireAuth,
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() => import('pages/StrategicElementsPage'))
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '/integrations/microsoft/sign_in',
      getComponent(nextState, cb) {
        const renderRoute = loadModule(cb);

        handleAppUpdate(() =>
          import('features/dataSources/MicrosoftAuthenticationRedirectPage')
        )
          .then(component => renderRoute(component))
          .catch(errorLoading(cb));
      },
    },

    {
      path: '*',
      getComponent(nextState, cb) {
        handleAppUpdate(() => import('pages/NotFoundPage'))
          .then(loadModule(cb))
          .catch(errorLoading(cb));
      },
    },
  ];
}
